import React, { useState, useEffect, useCallback, useRef } from "react";
import { Select, Spin, Tag, Button, Input } from "antd";
import { SearchOutlined, ReloadOutlined, DownOutlined, LoadingOutlined } from "@ant-design/icons";
import { connect } from "react-redux";
import {
  fetchModulesForSelector,
  searchModules,
  clearModuleSearch,
  fetchModule,
} from "../../store/actions/moduleActions";
import { debounce } from "lodash";

const { Option } = Select;

const ModuleSelector = ({
  value = [],
  onChange,
  placeholder = "Select modules",
  disabled = false,
  // Redux props
  selectorModules,
  selectorSearchResults,
  selectorPagination,
  selectorLoading,
  fetchModulesForSelector,
  searchModules,
  clearModuleSearch,
  fetchModule,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchPage, setSearchPage] = useState(1);
  const [selectedModules, setSelectedModules] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [preventClose, setPreventClose] = useState(false);
  const scrollContainerRef = useRef(null);
  const selectRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Create a stable debounced search function
  const debouncedSearch = useRef(
    debounce((searchValue, signal) => {
      if (searchValue.trim()) {
        searchModules(searchValue, { page: 1, perPage: 20, append: false, signal });
        setSearchPage(1);
      } else {
        clearModuleSearch();
        if (selectorModules.length === 0) {
          fetchModulesForSelector({ page: 1, perPage: 20, signal });
          setCurrentPage(1);
        }
      }
    }, 300)
  ).current;

  // Cleanup function to cancel ongoing requests
  const cancelPendingRequests = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  }, []);

  // Initialize selected modules from value prop
  useEffect(() => {
    const updateSelected = async () => {
      if (Array.isArray(value)) {
        // Start with currently selected modules that are still in value
        let newSelected = selectedModules.filter(m => value.includes(m.id));
        
        // Find missing IDs not in current selectedModules
        const missingIds = value.filter(id => !newSelected.find(m => m.id === id));
        
        if (missingIds.length > 0) {
          try {
            const missingModules = await Promise.all(
              missingIds.map(async (id) => {
                try {
                  return await fetchModule(id);
                } catch (e) {
                  console.error(`Failed to fetch module ${id}:`, e);
                  return null;
                }
              })
            );
            // Add fetched modules, filtering out any failures
            newSelected = [...newSelected, ...missingModules.filter(m => m !== null)];
          } catch (error) {
            console.error('Error fetching missing modules:', error);
          }
        }
        
        // Update state
        setSelectedModules(newSelected);
      }
    };
  
    updateSelected();
  }, [value, fetchModule, selectedModules]);

  // Cleanup effect to cancel requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Load initial modules when dropdown opens
  const handleDropdownVisibleChange = (open) => {
    setIsOpen(open);
    if (open && selectorModules.length === 0 && !searchText) {
      fetchModulesForSelector({ page: 1, perPage: 20 });
      setCurrentPage(1);
    }
  };

  // Handle search input change
  const handleSearch = (searchValue) => {
    setSearchText(searchValue);
    if (!searchValue.trim()) {
      // Reset search page when clearing search
      setSearchPage(1);
      // Cancel any pending requests when clearing search
      cancelPendingRequests();
    } else {
      // Cancel previous requests and get new signal for the new search
      const signal = cancelPendingRequests();
      debouncedSearch(searchValue, signal);
    }
  };

  // Load more modules
  const loadMoreModules = useCallback(async () => {
    console.log('loadMoreModules called', { isLoadingMore, selectorPagination });
    
    if (isLoadingMore || !selectorPagination) {
      console.log('Early return:', { isLoadingMore, hasPagination: !!selectorPagination });
      return;
    }
    
    const isSearching = searchText.trim() !== '';
    const currentPageNum = isSearching ? searchPage : currentPage;
    const nextPage = currentPageNum + 1;
    
    console.log('Load more details:', {
      isSearching,
      currentPageNum,
      nextPage,
      totalPages: selectorPagination.totalPages
    });
    
    if (nextPage > selectorPagination.totalPages) {
      console.log('No more pages to load');
      return;
    }

    setIsLoadingMore(true);
    
    try {
      if (isSearching) {
        console.log('Loading more search results');
        await searchModules(searchText, { page: nextPage, perPage: 20, append: true });
        setSearchPage(nextPage);
      } else {
        console.log('Loading more regular modules');
        await fetchModulesForSelector({ page: nextPage, perPage: 20, append: true });
        setCurrentPage(nextPage);
      }
    } catch (error) {
      console.error('Error loading more modules:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, selectorPagination, searchText, searchPage, currentPage, searchModules, fetchModulesForSelector]);

  // Handle scroll to load more
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;
    
    const isSearching = searchText.trim() !== '';
    const currentPageNum = isSearching ? searchPage : currentPage;
    
    // Debug logging
    console.log('Scroll Debug:', {
      scrollTop,
      scrollHeight,
      clientHeight,
      isNearBottom,
      isSearching,
      currentPageNum,
      totalPages: selectorPagination?.totalPages,
      hasMorePages: selectorPagination && currentPageNum < selectorPagination.totalPages,
      isLoadingMore
    });
    
    if (isNearBottom && !isLoadingMore && selectorPagination && currentPageNum < selectorPagination.totalPages) {
      console.log('Triggering loadMoreModules');
      loadMoreModules();
    }
  }, [searchText, searchPage, currentPage, selectorPagination, isLoadingMore, loadMoreModules]);

  // Handle module selection
  const handleClear = () => {
    setSelectedModules([]);
    onChange([]);
  };
  
  // Refresh modules
  const handleRefresh = () => {
    const signal = cancelPendingRequests();
    setCurrentPage(1);
    setSearchPage(1);
    if (searchText) {
      searchModules(searchText, { page: 1, perPage: 20, signal });
    } else {
      fetchModulesForSelector({ page: 1, perPage: 20, signal });
    }
  };
  
  const currentModules = searchText ? selectorSearchResults : selectorModules;
  const availableModules = currentModules.filter(
    module => !selectedModules.find(selected => selected.id === module.id)
  );
  
  const isSearching = searchText.trim() !== '';
  const currentPageNum = isSearching ? searchPage : currentPage;
  const hasMorePages = selectorPagination && currentPageNum < selectorPagination.totalPages;
  
  const onLocalChange = (newIds) => {
    // Get newly added IDs
    const addedIds = newIds.filter(id => !value.includes(id));
    // Get full modules for added IDs from current list
    const addedModules = addedIds
      .map(id => currentModules.find(m => m.id === id))
      .filter(Boolean);
    
    // Update selectedModules: keep existing that are still selected, add new ones
    const updatedSelected = selectedModules
      .filter(m => newIds.includes(m.id))
      .concat(addedModules);
    
    setSelectedModules(updatedSelected);
    onChange(newIds);
  };
  
  const dropdownRender = (menu) => (
    <div>
      {/* Search Header */}
      <div
        style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}
        onMouseDown={(e) => {
          e.preventDefault();
          setPreventClose(true);
        }}
        onMouseUp={() => setPreventClose(false)}
        onClick={(e) => e.stopPropagation()}
      >
        <Input
          placeholder="Search modules..."
          prefix={selectorLoading && searchText ? <LoadingOutlined /> : <SearchOutlined />}
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
          allowClear={false}
          onMouseDown={(e) => {
            e.stopPropagation();
            setPreventClose(true);
          }}
          onMouseUp={() => setPreventClose(false)}
          onFocus={() => setPreventClose(true)}
          onBlur={() => setTimeout(() => setPreventClose(false), 100)}
          onClick={(e) => e.stopPropagation()}
          suffix={
            <Button
              type="text"
              icon={<ReloadOutlined />}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setPreventClose(true);
                handleRefresh();
                setTimeout(() => setPreventClose(false), 100);
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                setPreventClose(true);
              }}
              onMouseUp={() => setPreventClose(false)}
              loading={selectorLoading}
            />
          }
        />
      </div>
  
      {/* Loading State for initial load */}
      {selectorLoading && currentModules.length === 0 && (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <Spin tip="Loading modules..." />
        </div>
      )}
  
      {/* Module List */}
      <div
        ref={scrollContainerRef}
        style={{ maxHeight: '300px', overflowY: 'auto' }}
        onScroll={handleScroll}
      >
        {menu}
        
        {/* Load More Button */}
        {hasMorePages && (
          <div
            style={{ padding: '8px', textAlign: 'center', borderTop: '1px solid #f0f0f0' }}
            onMouseDown={(e) => {
              e.preventDefault();
              setPreventClose(true);
            }}
            onMouseUp={() => setPreventClose(false)}
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              type="link"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setPreventClose(true);
                loadMoreModules();
                setTimeout(() => setPreventClose(false), 100);
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                setPreventClose(true);
              }}
              onMouseUp={() => setPreventClose(false)}
              loading={isLoadingMore}
              icon={!isLoadingMore ? <DownOutlined /> : null}
            >
              {isLoadingMore ? 'Loading...' : 'Load More'}
            </Button>
          </div>
        )}
        
        {/* Loading indicator for infinite scroll */}
        {isLoadingMore && (
          <div style={{ padding: '8px', textAlign: 'center' }}>
            <Spin size="small" />
          </div>
        )}
      </div>
  
      {/* Total count */}
      {selectorPagination && (
        <div style={{ padding: '4px 8px', fontSize: '11px', color: '#999', borderTop: '1px solid #f0f0f0' }}>
          Showing {currentModules.length} of {selectorPagination.total} modules
        </div>
      )}
    </div>
  );

  return (
    <div>
      <Select
        ref={selectRef}
        mode="multiple"
        placeholder={placeholder}
        value={value}
        onSelect={(value, option) => {
          onLocalChange([...selectedModules.map(m => m.id), value]);
          setSearchText('');
        }}
        onChange={onLocalChange}
        onDropdownVisibleChange={(open) => {
          if (!preventClose || !open) {
            handleDropdownVisibleChange(open);
          }
        }}
        dropdownRender={dropdownRender}
        disabled={disabled}
        style={{ width: '100%' }}
        open={isOpen}
        onBlur={(e) => {
          if (!preventClose) {
            // Only close if the blur is not going to an element inside the dropdown
            const relatedTarget = e.relatedTarget;
            if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
              // Add a small delay to allow for clicks on dropdown elements
              setTimeout(() => {
                if (!preventClose) {
                  setIsOpen(false);
                }
              }, 150);
            }
          }
        }}
        onFocus={() => setIsOpen(true)}
        allowClear
        onClear={handleClear}
        maxTagCount="responsive"
        getPopupContainer={(triggerNode) => triggerNode.parentNode}
        tagRender={(props) => {
          const { label, value: moduleId, closable, onClose } = props;
          const module = selectedModules.find(m => m.id === moduleId);
          
          return (
            <Tag
              closable={closable}
              onClose={onClose}
              style={{ marginRight: 3 }}
            >
              {module?.name || label}
            </Tag>
          );
        }}
      >
        {/* Render all available modules as options */}
        {availableModules.map(module => (
          <Option key={module.id} value={module.id} title={module.name}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontWeight: 500 }}>{module.name}</span>
              {module.description && (
                <span style={{ fontSize: '12px', color: '#666' }}>
                  {module.description.length > 60
                    ? `${module.description.substring(0, 60)}...`
                    : module.description
                  }
                </span>
              )}
              <span style={{ fontSize: '11px', color: '#999' }}>
                {module.videos?.length || 0} videos
              </span>
            </div>
          </Option>
        ))}
        {/* Also render selected modules to ensure they're available */}
        {selectedModules
          .filter(module => !availableModules.find(m => m.id === module.id))
          .map(module => (
            <Option key={module.id} value={module.id} style={{ display: 'none' }}>
              {module.name}
            </Option>
          ))}
      </Select>
  
      {/* Selected modules summary */}
      {selectedModules.length > 0 && (
        <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
          {selectedModules.length} module{selectedModules.length !== 1 ? 's' : ''} selected
          {selectedModules.length > 0 && (
            <Button
              type="link"
              size="small"
              onClick={handleClear}
              style={{ padding: '0 4px', fontSize: '12px' }}
            >
              Clear all
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  selectorModules: state.modules.selectorModules || [],
  selectorSearchResults: state.modules.selectorSearchResults || [],
  selectorPagination: state.modules.selectorPagination,
  selectorLoading: state.modules.selectorLoading || false,
});

export default connect(mapStateToProps, {
  fetchModulesForSelector,
  searchModules,
  clearModuleSearch,
  fetchModule,
})(ModuleSelector);
